import { useSelector } from 'react-redux'
import { TextAsset, VideoAsset } from 'models/Asset'
import { clearAssetsSelection, selectAsset as selectAssetAction, updateAssetInPreview } from 'actions/layer'
import { simulateMouseClick } from 'helpers/simulateMouseClick'
import { isEmpty } from 'lodash'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Rect, Transformer } from 'react-konva'
import { approximateEqual } from '~/Util'
import { useAction, useBind, useChanged, useComposeConst } from '~/hooks'
import { getAnchorParams, getCoordsFromOffset, getDragBoundingFunc, getDragEndPatch, getMinSizes, toRadians } from './lib'
import { selectCanvasPreviewPlayerBounds } from '~/selectors/preview'
import { createText, getTextMinimumWidth } from '~/features/konva-shapes/createText'
import { getAbsoluteAssetSize } from '~/helpers/getAbsoluteAssetSize'

const getTextMinWidth = (asset, scale, fallbackWidth) => {
  if (!(asset instanceof TextAsset) || scale.x <= 0) return fallbackWidth

  return Math.max(
    fallbackWidth,
    Math.ceil(getTextMinimumWidth(asset) * scale.x) + 1
  )
}

const getWrappedTextHeight = (asset, width, scale, boundingRect, fallbackHeight) => {
  if (!(asset instanceof TextAsset) || width <= 0 || scale.x <= 0 || scale.y <= 0) {
    return fallbackHeight
  }

  const { attrs } = createText(asset, {
    boundingRect,
    isTransform: true,
    onlyAttrs: true,
    textWidth: width / scale.x,
  })
  const height = attrs.height * scale.y

  return height > 0 ? height : fallbackHeight
}


export const EditingTransformer = memo(({
  asset,
  onlyMove,
  scale,
  boundingRect,
  layerRef,
  onTransformStart = () => { },
  onDragging = () => {},
  onTransforming = () => {},
  referenceAsset,
  isTransform,
}) => {
  const size = useMemo(() => (
    asset instanceof TextAsset
      ? createText(asset, {
        boundingRect,
        onlyAttrs: true,
        isTransform,
      }).attrs
      : getAbsoluteAssetSize(asset, referenceAsset)
  ), [ asset, boundingRect, referenceAsset, isTransform ])
  let { width, height } = size
  width *= scale.x
  height *= scale.y

  const rotateDisabled = [ VideoAsset, TextAsset ].find(Klass => asset instanceof Klass)
  const { settings, id, selected } = asset
  const { offset, rotation, position, flip, keepAspectRatio, position: anchor,
    outlineWidth = 0 } = settings

  const transformerRef = useRef()
  const singleClickTimeoutRef = useRef(false)
  const ref = useRef()

  const memoDragCoords = useRef({ x: 0, y: 0 })
  const pendingTransformRef = useRef(null)
  // Mutable Ref coordinates of the last move
  const pendingPositionRef = useRef({ x: 0, y: 0 })
  // requestAnimationFrame id
  const rafIdRef = useRef(null)
  const lastUpdateTime = useRef(0)

  const [ isDragStopped, setIsDragStopped ] = useState(false)

  const geometry = useChanged({ boundingRect, assetKonvaSize: { width, height } })

  const selectAsset = useAction(selectAssetAction, asset.id)
  const clearSelection = useAction(clearAssetsSelection)
  const updateAsset = useAction(updateAssetInPreview, id)
  const previewBounds = useSelector(selectCanvasPreviewPlayerBounds)

  const updateAssetSettings = useComposeConst(updateAsset, value => ({
    settings: {
      ...value,
    },
  }))

  const onUpdateOverlay = useComposeConst(
    updateAssetSettings,
    useBind(getDragEndPatch)
  )

  const dragBoundFunc = getDragBoundingFunc(position, geometry, previewBounds)


  const isCanvas = useCallback(e => {
    const { boundingRect } = geometry
    const minX = previewBounds.x - boundingRect.offsetX
    const minY = previewBounds.y + boundingRect.offsetY
    const maxX = previewBounds.x + boundingRect.width + boundingRect.offsetX
    const maxY = previewBounds.y + boundingRect.height
    return !(minX > e.clientX || maxX < e.clientX || minY > e.clientY || maxY < e.clientY)
  }, [ geometry, previewBounds ])

  const onOverlaySelect = useCallback(() => {
    selectAsset()
  }, [ selectAsset ])

  const handleOverlayClick = e => {
    if (e.evt.detail === 1) {
      // Timeout for the waiting for doubleclick debounce
      singleClickTimeoutRef.current = setTimeout(() => {
        onOverlaySelect()
      }, 150)
    // Synthetic event
    } else if (e.evt.detail === 0) {
      onOverlaySelect()
    }
  }

  const handleOverlayDbClick = e => {
    // This check is important to prevent self-calling by the synthetic event
    if (e.evt.detail === 2) {
      clearTimeout(singleClickTimeoutRef.current)
      clearSelection({ closeSettings: false })
      // Timeout is useful to wait for the updating after clearing the selection
      setTimeout(() => {
        // eslint-disable-next-line no-underscore-dangle
        simulateMouseClick(layerRef.current.canvas._canvas, {
          clientX: e.evt.clientX,
          clientY: e.evt.clientY,
        })
      })
    }
  }

  useEffect(() => {
    if (selected && !onlyMove && ref?.current) {
      transformerRef.current.nodes([ ref.current ])
      transformerRef.current.getLayer().batchDraw()
    } else if (transformerRef.current) {
      transformerRef.current.nodes([])
      transformerRef.current.getLayer().batchDraw()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ onlyMove, ref, selected, document.fullscreenElement ])

  const [ pos, setPos ] = useState({ x: 0, y: 0 })

  const handleGlobalMouseMove = useCallback(e => {
    if (isDragStopped) {
      if (isCanvas(e)) {
        ref.current.startDrag()
        setIsDragStopped(false)
      }
    }
  }, [ isDragStopped, isCanvas ])

  const handleDragStart = () => {
    setIsDragStopped(false)
  }

  const handleDragEnd = e => {
    const { rotation } = e.currentTarget.attrs
    const { x, y } = e.currentTarget.getClientRect()

    onUpdateOverlay({
      x,
      y,
      rotation,
      scale,
      boundingRect,
      anchor,
      assetKonvaSize: { width: width + outlineWidth, height: height + outlineWidth },
    })

    pendingPositionRef.current = { x: 0, y: 0 }
    // Stop the rAF loop after the move is complete
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    setIsDragStopped(false)
  }

  const offsetX = width ? width / 2 : undefined
  const offsetY = height ? height / 2 : undefined

  const hasTransformedRef = useRef(false)

  useEffect(() => {
    if (ref?.current && !isEmpty(size)) {
      // TODO: consider boxWidth for the correct rotation
      const newPos = getCoordsFromOffset(offset,
        { boundingRect, assetKonvaSize: { width, height }, rotation, outlineWidth, scale })
      setPos(newPos)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ boundingRect, settings, ref ])

  const getSizeAfterTransform = e => {
    const { target: node } = e
    const { width, height, x, y, rotation } = node.attrs
    const scaleX = node.scaleX()
    const scaleY = node.scaleY()
    const transformedWidth = Math.abs(width * scaleX)
    let transformedHeight = Math.abs(height * scaleY)

    if (asset instanceof TextAsset) {
      transformedHeight = getWrappedTextHeight(
        asset, transformedWidth, scale, boundingRect, transformedHeight
      )

      // Text is resized only horizontally. Its height is derived from word wrapping.
      node.height(transformedHeight)
      node.offsetY(transformedHeight / 2)
      if (transformerRef.current) {
        transformerRef.current.forceUpdate()
      }
    }

    return { size: {
      width: transformedWidth,
      height: transformedHeight,
    },
    pos: { x, y },
    rotation }
  }

  const handleTransformEnd = e => {
    if (!e.target) return
    const currentTime = performance.now()
    if (currentTime - lastUpdateTime.current < 16) return
    lastUpdateTime.current = currentTime

    hasTransformedRef.current = true
    const scaleX = e.target.scaleX()
    const scaleY = e.target.scaleY()
    const { size } = getSizeAfterTransform(e)
    const { width, height } = e.target.getClientRect({ skipStroke: true })
    let { x, y } = e.target.attrs

    x -= width / 2 - boundingRect.offsetX
    y -= height / 2 - boundingRect.offsetY

    const outline = outlineWidth / 2

    const r = e.target.attrs.rotation
    const rad = toRadians(r)
    x -= Math.abs(Math.cos(rad) * outline) + Math.abs(Math.sin(rad) * outline)
    y -= Math.abs(Math.cos(rad) * outline) + Math.abs(Math.sin(rad) * outline)

    if (asset instanceof TextAsset) {
      // Commit the transformed geometry to the Konva target before resetting scale.
      // Otherwise the Rect snaps back to its pre-transform width until React catches up.
      e.target.width(size.width)
      e.target.height(size.height)
      e.target.offsetX(size.width / 2)
      e.target.offsetY(size.height / 2)
    }

    e.target.scaleX(Math.sign(scaleX))
    e.target.scaleY(Math.sign(scaleY))

    // eslint-disable-next-line no-shadow
    const rotation = Math.round(e.target.rotation())

    const changedParams = { x, y, rotation, boundingRect }
    if (!approximateEqual(scaleX, 1) || !approximateEqual(scaleY, 1)) {
      changedParams.size = size
      // consider canvas scale
      changedParams.size.width = Math.round(changedParams.size.width / scale.x)
      changedParams.size.height = Math.round(changedParams.size.height / scale.y)
    }
    onUpdateOverlay({ ...changedParams, anchor, assetKonvaSize: { width, height } })
    onTransformStart(false)

    pendingTransformRef.current = null
    // Stop the rAF loop after the move is complete
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  // This function that runs on every RAF frame
  const animatePosition = useCallback(() => {
    // Last position
    const newPos = pendingPositionRef.current
    // Update React State once per frame
    onDragging(asset.id, { x: newPos.x, y: newPos.y })
    if (!asset.selected) {
      onOverlaySelect()
    }

    // Planning the next frame
    rafIdRef.current = requestAnimationFrame(animatePosition)
  }, [ asset, onDragging, onOverlaySelect ])

  // This function that runs on every RAF frame
  const animateTransform = useCallback(() => {
    // Last transformData
    const data = pendingTransformRef.current
    if (!data) {
      rafIdRef.current = null
      return
    }
    onTransforming(asset.id, data)
    // Planning the next frame
    rafIdRef.current = requestAnimationFrame(animateTransform)
  }, [ asset, onTransforming ])


  const onTransform = e => {
    pendingTransformRef.current = getSizeAfterTransform(e)
    // If the rAF cycle has not yet started, start it
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(animateTransform)
    }
  }

  const handleDragMove = e => {
    const currentTime = performance.now()
    if (currentTime - lastUpdateTime.current < 16) return
    lastUpdateTime.current = currentTime
    if (!isCanvas(e.evt) && !isDragStopped) {
      ref.current.stopDrag()
      setIsDragStopped(true)
    } else {
      // if (asset instanceof TextAsset) {
      //   const minY = e.target.height() / 2 + e.target.y() + previewBounds.top
      //   const maxY = e.target.y() - e.target.height() / 2 + previewBounds.top
      //   const minX = previewBounds.left + 2 - e.target.width()
      //   const maxX = previewBounds.right - 2 - e.target.width()
      //   const maxYOffset = 5
      //   if (minY < previewBounds.top) {
      //     e.target.y(-e.target.height() / 2)
      //   }
      //   if (maxY > previewBounds.bottom) {
      //     e.target.y(previewBounds.bottom + e.target.height() / 2 - previewBounds.top - maxYOffset)
      //   }
      //   if (e.target.x() < -minX) {
      //     e.target.x(-minX)
      //   }
      //   if (e.target.x() > maxX) {
      //     e.target.x(maxX)
      //   }
      // }
      memoDragCoords.current.x = e.target.x()
      memoDragCoords.current.y = e.target.y()
      // Fix moving text
      const offset = onlyMove && selected ? 1 : 0
      pendingPositionRef.current = {
        x: e.target.x() - offset,
        y: e.target.y() - offset,
      }

      // If the rAF cycle has not yet started, start it
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(animatePosition)
      }
    }
  }

  const params = {
    ...pos,
    width,
    height,
    offsetX,
    offsetY,
    rotation,
    ...(flip?.horizontal ? { scaleX: -1, offsetX } : {}),
    ...(flip?.vertical ? { scaleY: -1, offsetY } : {}),
  }

  const {
    radius,
    fill,
    enabledAnchors,
    stroke,
    anchorSize,
  } = getAnchorParams(asset, keepAspectRatio)
  const minSizes = getMinSizes(asset)
  const minWidth = asset instanceof TextAsset
    ? getTextMinWidth(asset, scale, minSizes.minWidth)
    : minSizes.minWidth
  const { minHeight } = minSizes

  useEffect(() => () => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('mousemove', handleGlobalMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove)
    }
  }, [ handleGlobalMouseMove ])

  return (
    <If condition={!document.fullscreenElement && __APP_PROFILE__ !== 'itochu'}>
      <Rect
        {...params}
        ref={ref}
        id={id}
        strokeWidth={onlyMove && selected ? 2 : outlineWidth}
        stroke={onlyMove && selected ? 'rgba(255, 255, 255, 1)' : 'rgba(0, 255, 0, 0)'}
        dragBoundFunc={dragBoundFunc}
        onDragEnd={handleDragEnd}
        draggable
        strokeScaleEnabled={false}
        onClick={handleOverlayClick}
        onDblClick={handleOverlayDbClick}
        onDragMove={handleDragMove}
        onDragStart={handleDragStart}
      />

      <If condition={selected && !onlyMove}>
        <Transformer
          ref={transformerRef}
          rotationSnaps={[ 90, 180, 270, 360 ]}
          anchorFill={fill}
          anchorStroke={stroke}
          borderStroke="white"
          anchorCornerRadius={radius}
          anchorSize={anchorSize}
          ignoreStroke
          padding={outlineWidth / 2}
          enabledAnchors={enabledAnchors}
          rotateAnchorOffset={15}
          rotateEnabled={!rotateDisabled}
          onTransformEnd={handleTransformEnd}
          onTransform={onTransform}
          onTransformStart={onTransformStart}
          boundBoxFunc={(oldBoundBox, newBoundBox) => {
            const widthTooSmall = Math.abs(newBoundBox.width) < minWidth
            const heightTooSmall = !(asset instanceof TextAsset)
              && Math.abs(newBoundBox.height) < minHeight

            if (widthTooSmall || heightTooSmall) {
              return oldBoundBox
            }
            return newBoundBox
          }}
        />
      </If>
    </If>
  )
})
